import React, { useState, useEffect } from 'react';
import MarkdownRender from 'components/MarkdownRender/textBox';
import ToastAlert from 'components/Alert/ToastAlert';
import MobileSupportTabBar from 'components/TabBars/MobileSupportTabBar';
import ArticleHeadlinesKeywordTable from 'components/Tables/ArticleHeadlinesKeywordTable';

// Model mapper to convert model values to user-friendly names
const MODEL_MAPPER = [
  { value: "writer_1", label: "Writer 1" },
  { value: "writer_2", label: "Writer 2" },
  { value: "writer_3", label: "Writer 3" },
  { value: "writer_4", label: "Writer 4" }
];

const ActionDetailPanel = ({ 
  selectedAction, 
  showDetailPanel, 
  onClose, 
  formatDate 
}) => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);
  
  // Responsive width state
  const [panelWidth, setPanelWidth] = useState('100vw');
  
  // Handle responsive width
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth <= 768 ? '100vw' : '50vw';
      console.log(`ActionDetailPanel - Screen width: ${window.innerWidth}px, Panel width: ${newWidth}`);
      setPanelWidth(newWidth);
    };
    
    // Set initial width
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get user-friendly model name from mapper
  const getModelDisplayName = (modelValue) => {
    const modelOption = MODEL_MAPPER.find(option => option.value === modelValue);
    return modelOption ? modelOption.label : modelValue || 'Unknown Model';
  };

  // Handle copy functionality
  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      setToast({
        show: true,
        message: 'Content copied to clipboard!',
        type: 'success'
      });
    } catch (err) {
      console.error("Failed to copy content: ", err);
      setToast({
        show: true,
        message: 'Failed to copy content',
        type: 'error'
      });
    }
  };

  // Handle row copy functionality for arrays
  const handleRowCopy = async (content, index) => {
    try {
      let textToCopy;
      if (typeof content === "object" && content !== null && !Array.isArray(content)) {
        // Handle object with question as key and answer as value
        const entries = Object.entries(content);
        textToCopy = entries.map(([question, answer]) => `Question: ${question}\n\nAnswer: ${answer}`).join('\n\n---\n\n');
      } else if (typeof content === 'string') {
        textToCopy = content;
      } else {
        textToCopy = JSON.stringify(content, null, 2);
      }
      
      await navigator.clipboard.writeText(textToCopy);
      setCopiedRowIndex(index);
      setToast({
        show: true,
        message: 'Row content copied to clipboard!',
        type: 'success'
      });

      // Reset the copied row indicator after 2 seconds
      setTimeout(() => {
        setCopiedRowIndex(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy row content: ", err);
      setToast({
        show: true,
        message: 'Failed to copy row content',
        type: 'error'
      });
    }
  };

  // Handle toast close
  const handleToastClose = () => {
    setToast({ ...toast, show: false });
  };


  // Custom styles for the panel
  const customStyles = {
    slidePanel: {
      position: 'fixed',
      top: '50px', // Add gap from header
      right: showDetailPanel ? 0 : `-${panelWidth}`,
      width: panelWidth,
      height: 'calc(100vh - 50px)', // Adjust height to account for header
      backgroundColor: '#fff',
      boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
      transition: 'right 0.3s ease-in-out',
      zIndex: 1050,
      overflowY: 'auto'
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1040,
      display: showDetailPanel ? 'block' : 'none'
    }
  };

  // Format input data for nice presentation
  const formatInputData = (data) => {
    if (!data) return { type: 'empty', content: 'No input data available' };
    
    let parsedData;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch {
        return { type: 'text', content: data };
      }
    } else {
      parsedData = data;
    }
    
    if (typeof parsedData === 'object' && parsedData !== null) {
      return { type: 'object', content: parsedData };
    }
    
    return { type: 'text', content: String(parsedData) };
  };

  // Format output data - markdown for strings, JSON for objects, array for lists
  const formatOutputData = (data) => {
    if (!data) return { type: 'empty', content: 'No output data available' };
    
    if (typeof data === 'string') {
      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed)) {
          return { type: 'array', content: parsed };
        }
        if (typeof parsed === 'object') {
          // Check if it's article headline format (has multiple model arrays)
          const modelKeys = Object.keys(parsed);
          const hasMultipleModelArrays = modelKeys.length > 1 && 
            modelKeys.every(key => Array.isArray(parsed[key]));
          if (hasMultipleModelArrays) {
            return { type: 'article_headlines', content: parsed };
          }
          return { type: 'json', content: JSON.stringify(parsed, null, 2) };
        }
        // If it's a string inside JSON, treat as markdown
        return { type: 'markdown', content: parsed };
      } catch {
        // If not JSON, treat as markdown
        return { type: 'markdown', content: data };
      }
    }
    
    if (Array.isArray(data)) {
      return { type: 'array', content: data };
    }
    
    if (typeof data === 'object' && data !== null) {
      // Check if it's article headline format (has multiple model arrays)
      const modelKeys = Object.keys(data);
      const hasMultipleModelArrays = modelKeys.length > 1 && 
        modelKeys.every(key => Array.isArray(data[key]));
      if (hasMultipleModelArrays) {
        return { type: 'article_headlines', content: data };
      }
      return { type: 'json', content: JSON.stringify(data, null, 2) };
    }
    
    return { type: 'text', content: String(data) };
  };


  // Render input data nicely
  const renderInputData = (inputData) => {
    const formatted = formatInputData(inputData);
    
    if (formatted.type === 'empty') {
      return <div className="text-muted fst-italic">{formatted.content}</div>;
    }
    
    if (formatted.type === 'object') {
      return (
        <div className="row g-2">
          {Object.entries(formatted.content).map(([key, value], index) => (
            <div key={index} className="col-12">
              <div className="card bg-light border-0">
                <div className="card-body p-3">
                  <div className="row">
                    <div className="col-12 col-md-3">
                      <strong className="text-primary small d-block mb-2">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                      </strong>
                    </div>
                    <div className="col-12 col-md-9">
                      <div className="small text-dark">
                        {key === 'model' && typeof value === 'string' 
                          ? `${getModelDisplayName(value)}` 
                          : Array.isArray(value)
                            ? value.join(', ')
                            : typeof value === 'object' 
                              ? JSON.stringify(value) 
                              : typeof value === 'string'
                                ? (
                                    <div 
                                      className="markdown-content"
                                      style={{ 
                                        backgroundColor: '#f8f9fa', 
                                        padding: '0.75rem', 
                                        borderRadius: '0.375rem',
                                        lineHeight: '1.6',
                                        fontSize: '0.85rem'
                                      }}
                                    >
                                      <MarkdownRender variant="content">
                                        {String(value)}
                                      </MarkdownRender>
                                    </div>
                                  )
                                : String(value)
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // Handle string input with markdown rendering
    if (formatted.type === 'text') {
      return (
        <div 
          className="markdown-content"
          style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '1rem', 
            borderRadius: '0.375rem',
            lineHeight: '1.6',
            fontSize: '0.9rem'
          }}
        >
          <MarkdownRender variant="content">
            {formatted.content}
          </MarkdownRender>
        </div>
      );
    }
    
    return (
      <div className="bg-light p-3 rounded">
        <span className="small">{formatted.content}</span>
      </div>
    );
  };

  // Render output data
  const renderOutputData = (outputData) => {
    const formatted = formatOutputData(outputData);
    
    if (formatted.type === 'empty') {
      return <div className="text-muted fst-italic">{formatted.content}</div>;
    }
    
    if (formatted.type === 'array') {
      return (
        <div>
          {/* Copy All Button */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">Generated Content ({formatted.content.length} items)</small>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleCopy(formatted.content.map((item, index) => {
                if (typeof item === "object" && item !== null && !Array.isArray(item)) {
                  const entries = Object.entries(item);
                  const qaText = entries.map(([question, answer]) => `Question: ${question}\nAnswer: ${answer}`).join('\n\n');
                  return `${index + 1}. ${qaText}`;
                } else if (typeof item === 'string') {
                  return `${index + 1}. ${item}`;
                } else {
                  return `${index + 1}. ${JSON.stringify(item)}`;
                }
              }).join('\n\n'))}
              title="Copy all content"
            >
              <i className="fas fa-copy"></i> Copy All
            </button>
          </div>
          
          {/* Array Table */}
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th scope="col" style={{ width: "60px", color: "black" }}>
                    #
                  </th>
                  <th scope="col" style={{ color: "black" }}>Content</th>
                  <th scope="col" style={{ width: "100px", color: "black" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {formatted.content.map((item, index) => (
                  <tr key={index}>
                    <th scope="row">{index + 1}</th>
                    <td>
                      <div
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          fontSize: "13px",
                          lineHeight: "1.5",
                        }}
                      >
                        {typeof item === "object" && item !== null && !Array.isArray(item) ? (
                          <div>
                            {Object.entries(item).map(([question, answer], entryIndex) => (
                              <div key={entryIndex}>
                                <div className="text-primary fw-bold mb-2" style={{ fontSize: "13px" }}>
                                  {question}
                                </div>
                                <div className="text-success" style={{ fontSize: "13px" }}>
                                  <MarkdownRender variant="tableCell">
                                    {answer}
                                  </MarkdownRender>
                                </div>
                                {entryIndex < Object.entries(item).length - 1 && (
                                  <hr className="my-2" style={{ opacity: 0.3 }} />
                                )}
                              </div>
                            ))}
                          </div>
                        ) : typeof item === "string" ? (
                          <MarkdownRender variant="content">
                            {item}
                          </MarkdownRender>
                        ) : (
                          <pre className="mb-0 small text-wrap">
                            {JSON.stringify(item, null, 2)}
                          </pre>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${
                          copiedRowIndex === index
                            ? "btn-success"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => handleRowCopy(item, index)}
                        title="Copy this row"
                      >
                        <i
                          className={`fas ${
                            copiedRowIndex === index ? "fa-check" : "fa-copy"
                          } me-1`}
                        ></i>
                        {copiedRowIndex === index ? "Copied!" : "Copy"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    
    if (formatted.type === 'markdown') {
      return (
        <div>
          {/* Copy Button */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">Generated Content</small>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleCopy(formatted.content)}
              title="Copy content"
            >
              <i className="fas fa-copy"></i> Copy
            </button>
          </div>
          
          {/* Markdown Content */}
          <div 
            className="markdown-content"
            style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '0.375rem',
              maxHeight: '400px',
              overflowY: 'auto',
              lineHeight: '1.6'
            }}
          >
            <MarkdownRender variant="content">
              {formatted.content}
            </MarkdownRender>
          </div>
        </div>
      );
    }
    
    if (formatted.type === 'article_headlines') {
      const modelKeys = Object.keys(formatted.content);
      const totalHeadlines = modelKeys.reduce((sum, key) => sum + (formatted.content[key]?.length || 0), 0);
      
      // Create tabs for the beautiful interface
      const tabs = modelKeys.map((modelName, index) => ({
        label: `Draft ${String.fromCharCode(65 + index)}`,
        element: (
          <ArticleHeadlinesKeywordTable
            data={formatted.content[modelName] || []}
            topic="Article Headlines"
          />
        ),
      }));
      
      return (
        <div>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="text-muted">Article Headlines ({totalHeadlines} total)</small>
          </div>
          
          {/* Beautiful Tabbed Interface */}
          <MobileSupportTabBar tabs={tabs} />
        </div>
      );
    }
    
    if (formatted.type === 'json') {
      return (
        <div>
          {/* Copy Button */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">JSON Data</small>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleCopy(formatted.content)}
              title="Copy JSON content"
            >
              <i className="fas fa-copy"></i> Copy
            </button>
          </div>
          
          <pre className="mb-0 small text-wrap" style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '1rem', 
            borderRadius: '0.375rem',
            fontSize: '0.8rem',
            lineHeight: '1.4',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {formatted.content}
          </pre>
        </div>
      );
    }
    
    return (
      <div className="bg-light p-3 rounded">
        <span className="small">{formatted.content}</span>
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div style={customStyles.overlay} onClick={onClose}></div>

      {/* Slide-in Detail Panel */}
      <div style={customStyles.slidePanel}>
        {selectedAction && (
          <div className="h-100 d-flex flex-column">
            {/* Panel Header */}
            <div className="border-bottom p-4 bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 fw-bold">Action Details</h5>
                  <small className="text-muted">
                    {selectedAction.action_type ? 
                      selectedAction.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                      'Action'
                    }
                  </small>
                </div>
              </div>
            </div>

            {/* Panel Content */}
            <div className="flex-grow-1 p-4 overflow-auto">
              {/* Action Info */}
              <div className="mb-4">
                <div className="row g-3">
                  <div className="col-4">
                    <div className="card bg-light border-0">
                      <div className="card-body p-3 text-center">
                        <div className="h5 fw-bold text-primary mb-1">
                          {selectedAction.credits_used || 0}
                        </div>
                        <small className="text-muted">Credits Used</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="card bg-light border-0">
                      <div className="card-body p-3 text-center">
                        <div className="h6 fw-bold text-success mb-1">
                          {selectedAction.status || 'Completed'}
                        </div>
                        <small className="text-muted">Status</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="card bg-light border-0">
                      <div className="card-body p-3 text-center">
                        <div className="h6 fw-bold text-info mb-1" style={{ fontSize: '0.9rem' }}>
                          {selectedAction.input_data && typeof selectedAction.input_data === 'object' && selectedAction.input_data.model
                            ? getModelDisplayName(selectedAction.input_data.model)
                            : selectedAction.input_data && typeof selectedAction.input_data === 'string'
                            ? (() => {
                                try {
                                  const parsed = JSON.parse(selectedAction.input_data);
                                  return parsed.model ? getModelDisplayName(parsed.model) : 'N/A';
                                } catch {
                                  return 'N/A';
                                }
                              })()
                            : 'N/A'
                          }
                        </div>
                        <small className="text-muted">AI Model</small>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <small className="text-muted">
                    <i className="fas fa-clock me-1"></i>
                    {formatDate(selectedAction.created_at)}
                  </small>
                </div>
              </div>

              {/* Input Data Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <i className="fas fa-arrow-right text-primary me-2"></i>
                  Input Parameters
                </h6>
                <div className="card border">
                  <div className="card-body">
                    {renderInputData(selectedAction.input_data)}
                  </div>
                </div>
              </div>

              {/* Output Data Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <i className="fas fa-arrow-left text-success me-2"></i>
                  Generated Output
                </h6>
                <div className="card border">
                  <div className="card-body">
                    {renderOutputData(selectedAction.output_data)}
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              {selectedAction.description && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3">Description</h6>
                  <div className="card bg-light border-0">
                    <div className="card-body">
                      <p className="mb-0 small">{selectedAction.description}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Panel Footer */}
            <div className="border-top p-4 bg-light">
              <button
                onClick={onClose}
                className="btn btn-secondary w-100"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast Alert */}
      <ToastAlert
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={handleToastClose}
        position="top-right"
        duration={3000}
      />
    </>
  );
};

export default ActionDetailPanel;
